import { Action, Reducer } from "redux";
import { AppThunkAction, remoteActions, ApplicationState, SetErrorAction } from ".";
import { ProcessingAction } from "./actions";
import { actions as AddonActions, IAddOn, SetAddonPrices } from "./AddOn";
import { actions as FooterActions } from "./Footer";
import { DeepClone, GetDateFromDateTime, GetIntFromString } from "../helperFunctions";
import { ParsableDate } from "@material-ui/pickers/constants/prop-types";

export const INSURANCE_START_DATE_CHANGED = "INSURANCE_START_DATE_CHANGED";
export const INSURANCE_SELECTED_COVERAGE_CHANGED = "INSURANCE_SELECTED_COVERAGE_CHANGED";
export const INSURANCE_GLASS_DEDUCTIBLE_CHANGED = "INSURANCE_GLASS_DEDUCTIBLE_CHANGED";
export const INSURANCE_SELF_RISK_CHANGED = "INSURANCE_SELF_RISK_CHANGED";
export const INSURANCE_RECEIVE_TYPE_CHANGED = "INSURANCE_RECEIVE_TYPE_CHANGED";
export const SINGLE_RECOMMENDED_COVERAGE_CHANGED = "SINGLE_RECOMMENDED_COVERAGE_CHANGED";
export const INSURANCE_SELECTED_NO_COVERAGE_CHANGED = "INSURANCE_SELECTED_NO_COVERAGE_CHANGED";
export const DEDUCTIBLE_INVALID = "DEDUCTIBLE_INVALID";
export const DEDUCTIBLE_VALID = "DEDUCTIBLE_VALID";

export enum InsurancePeriod {
  month = "mån",
  sixMonth = "halvår",
  year = "år",
  Monthly = "md",
  Yearly = "Helår",
  HalfYearly = "Halvår",
  Month = "Månad",
}

export interface InsuranceStartState {
  value?: Date;
  title: string;
  description: {
    title: string;
    text: string;
  };
  helperText?: string;
  helperTextFromUi?: string;
  hasError?: boolean;
  isDisabled: boolean;
}

interface Sortable {
  SortOrder: number;
}

export interface CoveragePrice {
  Amount: string;
  Currency: string;
  Period: InsurancePeriod;
  Discount?: string;
}

export interface CoverageItem extends Sortable {
  InternalCode: string;
  Name: string;
  Description?: string;
  ItemDeductible?: string;
}

export interface Deductible {
  Code: string;
  Name: string;
  IsSelected: boolean;
}

export interface Coverage extends Sortable {
  InternalCode: string;
  LongName: string;
  ShortName: string;
  Description: string;
  // DescriptionHeader: string;
  PrimaryPrice: CoveragePrice;
  SecondaryPrice: CoveragePrice;
  IsRecommended: boolean;
  CoverageItems: CoverageItem[];
  Deductibles?: Deductible[];
  GlassDeductibles?: Deductible[];
  CoverageAddons?: IAddOn[];
  CalculatedPrice: string;
  CalculatedPriceSecondary: string;
}

export interface InsuranceCoverageState {
  selectedCode?: string;
  selectedName?: string;
  recommendedTitle?: string;
  Coverages?: Coverage[];
  CoverageItems?: CoverageItem[];
  SelectedDeductible?: string;
  SelectedGlassDeductible?: string;
  showDeductibleError?: boolean;
  deductibleErrorMessage?: string;
  IsSingleRecommened?: boolean;
  isCarUnder3YearsAndNotImported?: boolean;
  ui?: {
    deductible?: {
      label?: string;
      helpHeader?: string;
      helpText?: string;
    };
    glassDeductible?: {
      label?: string;
      helpHeader?: string;
      helpText?: string;
    };
    coverageSelect?: {
      label?: string;
    };
  };
  vehicleInsuranceTax?: {
    amount: number;
    type: string;
    currency: string;
    text: string;
    readMoreText: string;
    url: string;
  };
}

export interface InsuranceReceiveValue {
  Code: string;
  Name: string;
}

export interface InsuranceReceiveType {
  selectedValue?: string;
  values?: InsuranceReceiveValue[];
  title?: string;
  help?: { title: string; description: string };
  label?: { id?: string; title: string };
}

export interface InsuranceState {
  startDate: InsuranceStartState;
  coverage?: InsuranceCoverageState;
  avatarEnabled?: boolean;
  insuranceReceive?: InsuranceReceiveType;
}

// for dev. purposes
const defaults: InsuranceState = {
  startDate: {
    title: "Insurance should active from...",
    value: GetDateFromDateTime(new Date()),
    description: {
      title: "Insurance start date",
      text: "Start date of insurance",
    },
    isDisabled: false,
  },
  coverage: {
    selectedCode: undefined,
    recommendedTitle: "Vi rekommendar",
    IsSingleRecommened: true,
    isCarUnder3YearsAndNotImported: false,
    SelectedDeductible: undefined,
    deductibleErrorMessage: "Vi behöver veta vilken självrisk du väljer.",
    ui: {
      deductible: {
        label: "Självrisk vid vagnskada?",
        helpHeader: "Här väljer du självrisk vid vagnskada",
        helpText:
          "Självrisken är den del av skadan som du betalar själv. Privatägt fordon: Välj mellan 2 500, 4 500, 7 000 eller 10 000 kronor. Företagsägt fordon: 10 % av pbb* men du som har företagsbil har också möjighet att välja fast självrisk, ring oss för prisuppgift.",
      },
      glassDeductible: {
        label: "Självrisk vid glasskada?",
        helpHeader: "Här väljer du självrisk vid glasskada",
        helpText:
          "Självrisken är den del av skadan som du betalar själv. Privatägt fordon: Välj mellan 2 500, 4 500, 7 000 eller 10 000 kronor. Företagsägt fordon: 10 % av pbb* men du som har företagsbil har också möjighet att välja fast självrisk, ring oss för prisuppgift.",
      },
    },
    Coverages: [
      {
        InternalCode: "2ca6b6cb-9e3a-4d22-b83c-e214d0b47be6",
        LongName: "Helförsäkring",
        ShortName: "Hel",
        SortOrder: 3,
        // DescriptionHeader: "Helförsäkring",
        Description:
          "Välj helförsäkring när bilen blir äldre än 3 år och  personvagnsgarantin har upphört att gälla. Så länge garantin gäller ska du välja halvförsäkring. Annars betalar du för något du inte behöver.",
        PrimaryPrice: {
          Amount: "524",
          Currency: "kr",
          Period: InsurancePeriod.month,
        },
        SecondaryPrice: {
          Amount: "6277",
          Currency: "kr",
          Period: InsurancePeriod.year,
        },
        IsRecommended: false,
        CoverageItems: [
          {
            InternalCode: "coverage_item_traffic",
            Name: "Trafik",
            Description:
              "Trafikförsäkringen ersätter skador på förare, passagerare, medtrafikanter, andra fordon och annans egendom som skadas vid trafikolycka.",
            SortOrder: 1,
          },
          {
            InternalCode: "coverage_item_theft",
            Name: "Stöld",
            Description:
              "Du får ersättning vid stöld, inbrott eller skadegörelse. Avmonterade fordonsdelar ska vara inlåsta i utrymme som endast du har tillgång till.",
            SortOrder: 2,
          },
          {
            InternalCode: "coverage_item_fire",
            Name: "Brand",
            Description: "Du får ersättning för skador på bilen som orsakats av brand, blixtnedslag eller explosion.",
            SortOrder: 6,
          },
          {
            InternalCode: "coverage_item_glass",
            Name: "Glas",
            Description:
              "Du får ersättning för skador på bilens vind-, sido- eller bakruta. Om du väljer att reparera istället för att byta rutan, blir självrisken 200 kronor.",
            SortOrder: 4,
          },
          {
            InternalCode: "coverage_item_rescue",
            Name: "Räddning",
            Description:
              "Du får ersättning för kostnader i samband med assistans vid trafikolycka eller annat driftstopp. Ingår även starthjälp, hemtransport och merkostnad för hotellnatt.",
            SortOrder: 3,
          },
          {
            InternalCode: "coverage_item_protection",
            Name: "Rättsskydd",
            Description: "Du får ersättning för kostnader vid vissa tvister som rör ditt bilägande.",
            SortOrder: 8,
          },
          {
            InternalCode: "coverage_item_fuel",
            Name: "Feltankning",
            Description:
              "Du får ersättning för kostnader för rengöring av tank, behållare, ledningar och följdskador när du har tankat fel vätska i bilens drivmedelstank eller fyllt på fel medel i annan behållare i bilen.\n",
            SortOrder: 5,
          },
          {
            InternalCode: "coverage_item_machine",
            Name: "Maskin- och elektronik",
            Description:
              "När ditt fordon är högst 10 år eller inte körts mer än 15 000 mil, kan du få du ersättning för maskin- och elektronikskada som brustit och gått sönder av sig själv utan yttre påverkan. ",
            SortOrder: 7,
          },
          {
            InternalCode: "coverage_item_bodyinsurance",
            Name: "Vagnskada",
            Description:
              "Du får ersättning för kostnader som uppkommit vid skada på bilen efter en trafikolycka, singelolycka eller skadegörelse.",
            SortOrder: 9,
          },
        ],
        CalculatedPrice: `524 kr/${InsurancePeriod.month}`,
        CalculatedPriceSecondary: `6277 kr/${InsurancePeriod.year}`,
      },
      {
        InternalCode: "5c5e14d4-ea31-4ac8-a9dd-624f91e78bc9",
        LongName: "Halvförsäkring",
        ShortName: "Halv",
        SortOrder: 2,
        // DescriptionHeader: "Halvförsäkring",
        Description:
          "Halvförsäkring passar bra till nya bilar som har garanti, eller äldre bilar som inte har så högt värde längre. Bilens värde blir din självrisk om bilen totalskadas och du saknar helförsäkring/vagnskadegaranti.Självriskerna för brand, stöld etc. hittar du i informationsrutorna till vänster.",
        PrimaryPrice: {
          Amount: "313",
          Currency: "kr",
          Period: InsurancePeriod.month,
        },
        SecondaryPrice: {
          Amount: "3749",
          Currency: "kr",
          Period: InsurancePeriod.year,
        },
        IsRecommended: false,
        CoverageItems: [
          {
            InternalCode: "coverage_item_traffic",
            Name: "Trafik",
            Description:
              "Trafikförsäkringen ersätter skador på förare, passagerare, medtrafikanter, andra fordon och annans egendom som skadas vid trafikolycka.",
            SortOrder: 1,
          },
          {
            InternalCode: "coverage_item_theft",
            Name: "Stöld",
            Description:
              "Du får ersättning vid stöld, inbrott eller skadegörelse. Avmonterade fordonsdelar ska vara inlåsta i utrymme som endast du har tillgång till.",
            SortOrder: 2,
          },
          {
            InternalCode: "coverage_item_fire",
            Name: "Brand",
            Description: "Du får ersättning för skador på bilen som orsakats av brand, blixtnedslag eller explosion.",
            SortOrder: 6,
          },
          {
            InternalCode: "coverage_item_glass",
            Name: "Glas",
            Description:
              "Du får ersättning för skador på bilens vind-, sido- eller bakruta. Om du väljer att reparera istället för att byta rutan, blir självrisken 200 kronor.",
            SortOrder: 4,
          },
          {
            InternalCode: "coverage_item_rescue",
            Name: "Räddning",
            Description:
              "Du får ersättning för kostnader i samband med assistans vid trafikolycka eller annat driftstopp. Ingår även starthjälp, hemtransport och merkostnad för hotellnatt.",
            SortOrder: 3,
          },
          {
            InternalCode: "coverage_item_protection",
            Name: "Rättsskydd",
            Description: "Du får ersättning för kostnader vid vissa tvister som rör ditt bilägande.",
            SortOrder: 8,
          },
          {
            InternalCode: "coverage_item_fuel",
            Name: "Feltankning",
            Description:
              "Du får ersättning för kostnader för rengöring av tank, behållare, ledningar och följdskador när du har tankat fel vätska i bilens drivmedelstank eller fyllt på fel medel i annan behållare i bilen.\n",
            SortOrder: 5,
          },
          {
            InternalCode: "coverage_item_machine",
            Name: "Maskin- och elektronik",
            Description:
              "När ditt fordon är högst 10 år eller inte körts mer än 15 000 mil, kan du få du ersättning för maskin- och elektronikskada som brustit och gått sönder av sig själv utan yttre påverkan. ",
            SortOrder: 7,
          },
        ],
        CalculatedPrice: `313 kr/${InsurancePeriod.month}`,
        CalculatedPriceSecondary: `3749 kr/${InsurancePeriod.year}`,
      },
      {
        InternalCode: "07adade7-3bd5-4335-b389-fbcfa964605a",
        LongName: "Trafikförsäkring",
        ShortName: "Trafik",
        SortOrder: 1,
        // DescriptionHeader: "Trafikförsäkring",
        Description:
          "Tänk på att trafikförsäkringen inte täcker skador på din egen bil. Denna försäkring gäller bara för personskador och andras egendom som du skadar med din bil. Har du en äldre bil med lågt värde bör du ändå välja halvförsäkring för att få hjälp med bärgning.",
        PrimaryPrice: {
          Amount: "216",
          Currency: "kr",
          Period: InsurancePeriod.month,
        },
        SecondaryPrice: {
          Amount: "2589",
          Currency: "kr",
          Period: InsurancePeriod.year,
        },
        IsRecommended: false,
        CoverageItems: [
          {
            InternalCode: "coverage_item_traffic",
            Name: "Trafik",
            Description:
              "Trafikförsäkringen ersätter skador på förare, passagerare, medtrafikanter, andra fordon och annans egendom som skadas vid trafikolycka.",
            SortOrder: 1,
          },
        ],
        CalculatedPrice: `216 kr/${InsurancePeriod.month}`,
        CalculatedPriceSecondary: `2589 kr/${InsurancePeriod.year}`,
      },
    ],
  },
};

export type ValidAction = {
  type:
    | typeof INSURANCE_START_DATE_CHANGED
    | typeof INSURANCE_SELECTED_COVERAGE_CHANGED
    | typeof INSURANCE_SELF_RISK_CHANGED
    | typeof INSURANCE_GLASS_DEDUCTIBLE_CHANGED
    | typeof INSURANCE_RECEIVE_TYPE_CHANGED
    | typeof SINGLE_RECOMMENDED_COVERAGE_CHANGED
    | typeof INSURANCE_SELECTED_NO_COVERAGE_CHANGED
    | typeof DEDUCTIBLE_INVALID
    | typeof DEDUCTIBLE_VALID
    | ProcessingAction
    | SetErrorAction;
  payload: { value: any };
};

export const reducer: Reducer<InsuranceState> = (state: InsuranceState = defaults, input: Action): InsuranceState => {
  const action = input as ValidAction;

  switch (action?.type) {
    case INSURANCE_START_DATE_CHANGED:
      const newStartDateState = DeepClone(state.startDate);
      const dateNow = new Date();
      if (action.payload.value) {
        newStartDateState.value = GetDateFromDateTime(action.payload.value as Date);
        if (newStartDateState.value) {
          newStartDateState.hasError =
            newStartDateState?.value < new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate()) ? true : false;
        } else {
          newStartDateState.hasError = true;
        }
        newStartDateState.helperText = newStartDateState.hasError ? newStartDateState.helperTextFromUi : undefined;
      } else {
        newStartDateState.hasError = true;
        newStartDateState.value = undefined;
        newStartDateState.helperText = newStartDateState.hasError ? newStartDateState.helperTextFromUi : undefined;
      }
      return { ...state, startDate: newStartDateState };

    case INSURANCE_SELECTED_COVERAGE_CHANGED:
      const newCoverageState = DeepClone(state.coverage);
      if (newCoverageState) {
        newCoverageState.selectedCode = action.payload.value as string;
      }
      return { ...state, coverage: newCoverageState };

    case INSURANCE_SELECTED_NO_COVERAGE_CHANGED:
      const coverageState = DeepClone(state.coverage);
      if (coverageState) {
        coverageState.selectedCode = action.payload.value as string;
      }
      return { ...state, coverage: coverageState };

    case INSURANCE_SELF_RISK_CHANGED:
      const newState = DeepClone(state.coverage);
      if (newState) {
        newState.SelectedDeductible = action.payload.value as string;
        newState.showDeductibleError = false;
      }
      return { ...state, coverage: newState };

    case INSURANCE_GLASS_DEDUCTIBLE_CHANGED:
      const glassState = DeepClone(state.coverage);
      if (glassState) {
        glassState.SelectedGlassDeductible = action.payload.value as string;
      }
      return { ...state, coverage: glassState };

    case INSURANCE_RECEIVE_TYPE_CHANGED:
      const newInsuranceReceiveState = DeepClone(state.insuranceReceive);
      if (newInsuranceReceiveState) {
        newInsuranceReceiveState.selectedValue = action.payload.value as string;
      }
      return { ...state, insuranceReceive: newInsuranceReceiveState };

    case SINGLE_RECOMMENDED_COVERAGE_CHANGED:
      const coverage = DeepClone(state.coverage);
      if (coverage) {
        coverage.IsSingleRecommened = action.payload.value as boolean;
      }
      return { ...state, coverage: coverage };

    case DEDUCTIBLE_INVALID: {
      const coverage = DeepClone(state.coverage);
      if (coverage) {
        coverage.showDeductibleError = true;
      }
      return { ...state, coverage: coverage };
    }

    case DEDUCTIBLE_VALID: {
      const coverage = DeepClone(state.coverage);
      if (coverage) {
        coverage.showDeductibleError = false;
      }
      return { ...state, coverage: coverage };
    }

    default:
      return state;
  }
};

export const actions = {
  insuranceStartDateChanged:
    (value: ParsableDate): AppThunkAction<ValidAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: INSURANCE_START_DATE_CHANGED,
        payload: { value: value },
      });

      dispatch(remoteActions.requestPriceData());
    },
  insuranceCoverageChanged:
    (value: string): AppThunkAction<ValidAction | AppThunkAction<{}>> =>
    (dispatch, getState) => {
      dispatch({
        type: INSURANCE_SELECTED_COVERAGE_CHANGED,
        payload: { value: value },
      });

      const state = getState() as ApplicationState;
      const currentCoverageState = state?.insurance?.coverage;
      const currentCoverage = currentCoverageState?.Coverages?.find(i => i.InternalCode === currentCoverageState.selectedCode);

      dispatch(AddonActions.coverageChanged(currentCoverage?.CoverageAddons?.map(x => x.InternalCode) as string[]));

      const newState = getState() as ApplicationState;
      const selectedAddonInternalCodes = newState?.addon?.selectedAddOnInternalCodes ?? [];
      const list =
        currentCoverage?.CoverageAddons?.filter(i => !!i.InternalCode && selectedAddonInternalCodes?.indexOf(i.InternalCode) !== -1).map(
          i => i.InternalCode as string,
        ) ?? [];

      dispatch(FooterActions.coverageChanged(currentCoverage as Coverage, list));
    },

  insuranceSeflRiskChanged:
    (value: string): AppThunkAction<ValidAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: INSURANCE_SELF_RISK_CHANGED,
        payload: { value: value },
      });

      dispatch(remoteActions.requestPriceData());
    },

  insuranceGlassDeductibleChanged:
    (value: string): AppThunkAction<ValidAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: INSURANCE_GLASS_DEDUCTIBLE_CHANGED,
        payload: { value: value },
      });

      dispatch(remoteActions.requestPriceData());
    },

  insuranceReceiveTypeChanged:
    (value: string): AppThunkAction<ValidAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: INSURANCE_RECEIVE_TYPE_CHANGED,
        payload: { value: value },
      });
    },
  singleRecommendedCoverageChanged:
    (value: boolean): AppThunkAction<ValidAction | AppThunkAction<{}>> =>
    dispatch => {
      dispatch({
        type: SINGLE_RECOMMENDED_COVERAGE_CHANGED,
        payload: { value: value },
      });
    },
};

export function GetCalculatedPrice(coveragePrice: CoveragePrice, coverage: Coverage, selectedAddonInternalCodes: string[]): string {
  let coverageAddons = coverage.CoverageAddons?.filter(x => selectedAddonInternalCodes.includes(x.InternalCode ?? "")) as IAddOn[];
  coverageAddons = DeepClone(coverageAddons);
  const addonSum = SetAddonPrices(coveragePrice, coverageAddons);
  return `${GetIntFromString(coveragePrice.Amount) - addonSum} ${coveragePrice.Currency}/${coveragePrice.Period}`;
}
